import { type Input } from "~/components/market-data/MarketDataBrowser";
import { type LocationForSelector } from "~/components/ui/LocationSelector";
import { isEqual, orderBy } from "~/lib/lodash";
import { joinModels } from "~/lib/queryParams";
import { arrayHasValues, getId } from "~/lib/utils";
import { type NullableAuthenticatedUser } from "~/services/auth/fetchAuthenticatedUser";
import {
  getDefaultLevels,
  getRangeFromInput,
  getSizeFromInput,
} from "~/services/market-data/market-data-filters/utils";
import {
  DefaultPercentileRangeWidth,
  DefaultTargetPercentiles,
  generatePercentileBounds,
} from "~/services/percentiles";

export type BuildQueryStringOptions = {
  user: NullableAuthenticatedUser;
  input: Input;
  defaultLocations: LocationForSelector[];
};

export const buildURLSearchParams = (params: BuildQueryStringOptions, options: { canAccessFiguresAiV2: boolean }) => {
  const { user, input, defaultLocations } = params;

  const urlSearchParams = new URLSearchParams();

  urlSearchParams.append("currency", input.currency.code);
  urlSearchParams.append("measure", input.measure);

  if (!isEqual(input.levels, getDefaultLevels(user))) {
    urlSearchParams.append("levels", input.levels.join(","));
  }

  if (arrayHasValues(input.benchmarkLevelIds)) {
    urlSearchParams.append("benchmark-levels", input.benchmarkLevelIds.join(","));
  }

  // Market

  if (input.presetId) {
    urlSearchParams.append("preset-id", `${input.presetId}`);
  }

  if (input.market.jobIds.length) {
    urlSearchParams.append("jobs", input.market.jobIds.join(","));
  }

  const locationsIds = orderBy(input.market.locations.map(getId));
  const defaultLocationsIds = orderBy(defaultLocations.map(getId));
  if (!isEqual(locationsIds, defaultLocationsIds)) {
    urlSearchParams.append("locations", locationsIds.join(","));
  }

  if (input.showCompanyEmployees === false) {
    urlSearchParams.append("show-company-employees", "false");
  }

  if (options.canAccessFiguresAiV2) {
    const headcount = getRangeFromInput(input.market);

    if (headcount !== null) urlSearchParams.append("headcount", headcount);
  } else {
    const { min, max } = getSizeFromInput(input.market);

    if (min !== null) urlSearchParams.append("size.min", `${min}`);
    if (max !== null) urlSearchParams.append("size.max", `${max}`);
  }
  if (input.market.fundingRounds.length) {
    urlSearchParams.append("funding-rounds", input.market.fundingRounds.join(","));
  }
  if (input.market.labels.length) {
    urlSearchParams.append("labels", joinModels(input.market.labels));
  }

  if (input.market.industry) {
    urlSearchParams.append("industry", input.market.industry.id.toString());
  }

  if (input.market.industries.length) {
    urlSearchParams.append("industries", joinModels(input.market.industries));
  }

  // Compare

  if (input.compare.enabled) {
    urlSearchParams.append("compare-with-jobs", "true");

    if (input.compare.jobIds.length) {
      urlSearchParams.append("x.jobs", input.compare.jobIds.join(","));
    }

    const compareLocationIds = orderBy(input.compare.locations.map(getId));
    if (!isEqual(compareLocationIds, defaultLocationsIds)) {
      urlSearchParams.append("x.locations", compareLocationIds.join(","));
    }

    if (options.canAccessFiguresAiV2) {
      const headcount = getRangeFromInput(input.compare);

      if (headcount !== null) urlSearchParams.append("x.headcount", headcount);
    } else {
      const { min, max } = getSizeFromInput(input.compare);

      if (min !== null) urlSearchParams.append("x.size.min", `${min}`);
      if (max !== null) urlSearchParams.append("x.size.max", `${max}`);
    }
    if (input.compare.fundingRounds.length) {
      urlSearchParams.append("x.funding-rounds", input.compare.fundingRounds.join(","));
    }
    if (input.compare.labels.length) {
      urlSearchParams.append("x.labels", joinModels(input.compare.labels));
    }

    if (input.compare.industry) {
      urlSearchParams.append("x.industry", input.compare.industry.id.toString());
    }

    if (input.compare.industries.length) {
      urlSearchParams.append("x.industries", joinModels(input.compare.industries));
    }
  }

  // Percentiles

  const bounds = generatePercentileBounds({
    targetPercentile: user?.company.globalTargetPercentile ?? DefaultTargetPercentiles.middle,
    rangeWidth: user?.company.percentileRangeWidth ?? DefaultPercentileRangeWidth,
  });
  if (input.percentiles.low !== bounds.low) {
    urlSearchParams.append("percentile.low", `${input.percentiles.low}`);
  }
  if (input.percentiles.middle !== bounds.middle) {
    urlSearchParams.append("percentile.middle", `${input.percentiles.middle}`);
  }
  if (input.percentiles.high !== bounds.high) {
    urlSearchParams.append("percentile.high", `${input.percentiles.high}`);
  }

  return urlSearchParams;
};
